// Meetings overview: timeline of meetings + new meeting entry point.

import { html, LitElement } from "lit";
import { ApiError, hosted, meetings } from "../lib/api";
import { navigate, store } from "../lib/store";
import type { Meeting } from "../lib/types";

function stateBadge(state: Meeting["state"]) {
  const cls: Record<Meeting["state"], string> = {
    recording: "badge-danger",
    importing: "badge-accent",
    summarizing: "badge-warn",
    complete: "badge-ok",
    failed: "badge-danger",
  };
  return html`<span class="badge ${cls[state]}">${state}</span>`;
}

export class MmMeetingList extends LitElement {
  private items: Meeting[] = [];
  private loaded = false;
  private error = "";

  override createRenderRoot(): HTMLElement {
    return this;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    void this.load();
  }

  private async load(): Promise<void> {
    try {
      this.items = (await meetings.list()).meetings;
      this.error = "";
    } catch (e) {
      this.error = e instanceof ApiError ? e.message : "failed to load meetings";
    }
    this.loaded = true;
    this.requestUpdate();
  }

  private async newMeeting(): Promise<void> {
    const title = `Meeting ${new Date().toLocaleString()}`;
    const created = await meetings.create(title);
    navigate(`/meetings/${created.meeting.id}`);
  }

  private async newHostedMeeting(): Promise<void> {
    const title = `Meeting ${new Date().toLocaleString()}`;
    const created = await hosted.create(title);
    navigate(`/meetings/${created.meeting.id}`);
  }

  override render() {
    return html`
      <section class="summary">
        <p class="eyebrow">Workspace</p>
        <h1>Meetings</h1>
        <div class="toolbar">
          ${store.get().hostedMeetings
            ? html`<button class="primary-button" @click=${this.newHostedMeeting}>
                ☎ New hosted meeting
              </button>`
            : ""}
          <button class=${store.get().hostedMeetings ? "ghost-button" : "primary-button"}
            @click=${this.newMeeting}>
            ● Record this device
          </button>
          <a class="ghost-button" href="#/import">Import audio</a>
        </div>
      </section>
      ${this.error
        ? html`<div class="message-card message-card-error"><p>${this.error}</p></div>`
        : ""}
      <section>
        ${!this.loaded
          ? html`<p class="empty">Loading…</p>`
          : this.items.length === 0
            ? html`<p class="empty">
                No meetings yet. Start one, or import a recording.
              </p>`
            : html`<div class="timeline">
                ${this.items.map(
                  (m) => html`
                    <a class="timeline-item" href="#/meetings/${m.id}">
                      <div class="timeline-head">
                        <strong>${m.title}</strong>
                        ${stateBadge(m.state)}
                      </div>
                      <p class="muted">
                        ${new Date(m.created_at).toLocaleString()} ·
                        ${m.billed_stt_seconds}s transcribed
                        ${m.error ? html` · <span class="mono">${m.error}</span>` : ""}
                      </p>
                    </a>
                  `,
                )}
              </div>`}
      </section>
    `;
  }
}

customElements.define("mm-meeting-list", MmMeetingList);
